import { QuestionnaireResponseItem } from '@medplum/fhirtypes'
import { AdapterOptions } from '../../../../../adapters/types'
import { RocFormData } from '../../../../types/RocFormData'
import { RocFormField } from '../../../../types/RocFormField'
import { RocAssessmentQuestionnaireItem } from '../../../../types/questionnaire'
import { getAnswerValuesFromItems } from '../../../../../helpers'
import { BaseSubsectionAdapter } from '../../../../../adapters/BaseSubsectionAdapter'
import { FlatAnswerMutation } from '../../../../../adapters/types'

/**
 * Risk Assessment subsection adapter
 * Handles complex risk assessments including ScoredChecklist components (18 fields)
 * This is one of the more complex subsections with various risk factors and safety measures
 */
export class RiskAssessmentAdapter extends BaseSubsectionAdapter<RocFormData> {
  extractFormData(
    flatAnswers: Record<string, QuestionnaireResponseItem>,
    _options?: AdapterOptions
  ): Partial<RocFormData> {
    const flatAnswersArray = Object.values(flatAnswers)

    // Hospitalization risk assessment
    const hospitalizationRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK
    )?.at(0)

    const hospitalizationRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS
    )

    // Hospitalization Risk Factors Other
    const hospitalizationRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
    )?.at(0)

    // Emergency planning
    const triageCode = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE
    )?.at(0)

    const emergencyPlan = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN
    )?.at(0)

    const safeRoom = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM
    )

    // Fire safety assessment
    const fireAssessmentOxygen = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN
    )?.at(0)

    const fireSafetyPrecautions = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS
    )

    // Homebound status
    const homeboundStatus = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS
    )?.at(0)

    // Infection risk assessment
    const infectionRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK
    )?.at(0)

    const infectionRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS
    )
    const infectionRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER
    )?.at(0)

    // Fall risk assessment (ScoredChecklist component)
    const fallRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK
    )?.at(0)

    const fallRiskFactors = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS
    )

    const fallRiskFactorsOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER
    )?.at(0)

    const fallRiskAssessment = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT
    )

    // Financial and environmental safety
    const financeRisk = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK
    )?.at(0)

    const financeRiskOther = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER
    )?.at(0)

    const environmentSafety = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY
    )

    const nurseSafetyRisks = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS
    )

    const safetyMeasures = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES
    )

    const notes = getAnswerValuesFromItems(
      flatAnswersArray,
      RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES
    )?.at(0)

    return {
      [RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK]: hospitalizationRisk,
      [RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS]: hospitalizationRiskFactors,
      [RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER]:
        hospitalizationRiskFactorsOther,
      [RocFormField.RISK_ASSESSMENT_TRIAGE_CODE]: triageCode,
      [RocFormField.RISK_ASSESSMENT_EMERGENCY_PLAN]: emergencyPlan,
      [RocFormField.RISK_ASSESSMENT_SAFE_ROOM]: safeRoom,
      [RocFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN]: fireAssessmentOxygen,
      [RocFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS]: fireSafetyPrecautions,
      [RocFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS]: homeboundStatus,
      [RocFormField.RISK_ASSESSMENT_INFECTION_RISK]: infectionRisk,
      [RocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS]: infectionRiskFactors,
      [RocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER]: infectionRiskFactorsOther,
      [RocFormField.RISK_ASSESSMENT_FALL_RISK]: fallRisk,
      [RocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS]: fallRiskFactors,
      [RocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER]: fallRiskFactorsOther,
      [RocFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT]: fallRiskAssessment,
      [RocFormField.RISK_ASSESSMENT_FINANCE_RISK]: financeRisk,
      [RocFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER]: financeRiskOther,
      [RocFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY]: environmentSafety,
      [RocFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS]: nurseSafetyRisks,
      [RocFormField.RISK_ASSESSMENT_SAFETY_MEASURES]: safetyMeasures,
      [RocFormField.RISK_ASSESSMENT_NOTES]: notes
    }
  }

  buildFlatAnswers(): FlatAnswerMutation[] {
    const mutations: FlatAnswerMutation[] = []

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER,
        value: this.getFormDataField(
          RocFormField.RISK_ASSESSMENT_HOSPITALIZATION_RISK_FACTORS_OTHER
        )
      })
    }
    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_TRIAGE_CODE)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_TRIAGE_CODE,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_TRIAGE_CODE)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_EMERGENCY_PLAN)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_EMERGENCY_PLAN,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_EMERGENCY_PLAN)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_SAFE_ROOM)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFE_ROOM,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_SAFE_ROOM)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_FIRE_ASSESSMENT_OXYGEN)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_FIRE_SAFETY_PRECAUTIONS)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_HOMEBOUND_STATUS,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_HOMEBOUND_STATUS)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_INFECTION_RISK)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_INFECTION_RISK)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_INFECTION_RISK_FACTORS_OTHER)
      })
    }
    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_FALL_RISK)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_FALL_RISK)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_FALL_RISK_FACTORS_OTHER)
      })
    }
    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FALL_RISK_ASSESSMENT,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_FALL_RISK_ASSESSMENT)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_FINANCE_RISK)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_FINANCE_RISK)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_FINANCE_RISK_OTHER,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_FINANCE_RISK_OTHER)
      })
    }
    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_ENVIRONMENT_SAFETY,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_ENVIRONMENT_SAFETY)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS)) {
      mutations.push({
        linkId:
          RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NURSE_SAFETY_RISKS,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_NURSE_SAFETY_RISKS)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_SAFETY_MEASURES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_SAFETY_MEASURES,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_SAFETY_MEASURES)
      })
    }

    if (this.formDataFieldExists(RocFormField.RISK_ASSESSMENT_NOTES)) {
      mutations.push({
        linkId: RocAssessmentQuestionnaireItem.MEDICAL_BACKGROUND_RISK_ASSESSMENT_NOTES,
        value: this.getFormDataField(RocFormField.RISK_ASSESSMENT_NOTES)
      })
    }

    return mutations
  }
}
